import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, GitMerge, Mail, Phone, Plus, Search, Settings, Stethoscope, Trash2, Upload, UserCheck, UserX, X } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import InitialAvatar from '../components/InitialAvatar';
import { AVATAR_DIMENSION_PX, AVATAR_JPEG_QUALITY, renderAvatarBlob } from '../lib/avatarUpload';
import { cn } from '../utils/cn';
import { useI18n } from '../i18n/LanguageContext';
import type { SupportedLanguage } from '../i18n/translations';
import {
  PatientFormState,
  PatientRow,
  PatientStatus,
  Sex,
  emptyFormState,
  formatAllergies,
  formatMedications,
  formatProblems,
  mapPatientFromApi,
  statusColors,
  statusLabels,
  statuses,
} from '../features/patients/domain';
import { FlagIcons } from '../features/patients/components/PatientDetail';
import { TextField } from '../ui/fields';
import { AddressFields } from '../components/AddressFields';

type PhraseTranslator = (phrase: string) => string;
type PatientModalTab = 'profile' | 'address' | 'coverage' | 'clinical';
type ClientView = {
  id: string;
  name: string;
  statusFilter: PatientStatus | 'All';
  providerFilter: string;
  sexFilter: 'All' | Sex;
  sortBy: 'name' | 'mrn' | 'lastVisit' | 'created';
  isDefault?: boolean;
};
type ClientCreateFieldId =
  | 'middleName'
  | 'genderIdentity'
  | 'phone'
  | 'email'
  | 'primaryProvider'
  | 'insurance'
  | 'emergencyContact'
  | 'clinicalNotes';

const configurableClientFields: Array<{ id: ClientCreateFieldId; label: string }> = [
  { id: 'middleName', label: 'Middle name' },
  { id: 'genderIdentity', label: 'Gender identity' },
  { id: 'phone', label: 'Phone' },
  { id: 'email', label: 'Email' },
  { id: 'primaryProvider', label: 'Primary provider' },
  { id: 'insurance', label: 'Insurance payer' },
  { id: 'emergencyContact', label: 'Emergency contact' },
  { id: 'clinicalNotes', label: 'Notes' },
];

const defaultCreateFieldSettings: Record<ClientCreateFieldId, { visible: boolean; required: boolean }> = {
  middleName: { visible: true, required: false },
  genderIdentity: { visible: true, required: false },
  phone: { visible: true, required: false },
  email: { visible: true, required: false },
  primaryProvider: { visible: true, required: false },
  insurance: { visible: true, required: false },
  emergencyContact: { visible: true, required: false },
  clinicalNotes: { visible: true, required: false },
};

const localeByLanguage: Record<SupportedLanguage, string> = {
  en: 'en-US',
  es: 'es-ES',
  it: 'it-IT',
};

const patientEsOverrides: Record<string, string> = {
  Patients: 'Pacientes',
  'List of Patients': 'Lista de pacientes',
  'Search patients by name, MRN, phone, email': 'Buscar pacientes por nombre, MRN, telefono o correo',
  'New Patient': 'Nuevo paciente',
  Hide: 'Ocultar',
  Show: 'Mostrar',
  filters: 'filtros',
  'Sort: Name': 'Ordenar: nombre',
  'Sort: MRN': 'Ordenar: MRN',
  'Sort: Last visit': 'Ordenar: ultima visita',
  'Sort: Created': 'Ordenar: creado',
  Error: 'Error',
  'Loading patients...': 'Cargando pacientes...',
  'Edit Patient': 'Editar paciente',
  'Create Patient': 'Crear paciente',
  'Prefill mock data': 'Rellenar datos de prueba',
  Close: 'Cerrar',
  'Loading patient details...': 'Cargando detalles del paciente...',
  'Patient avatar': 'Avatar del paciente',
  'Upload photo': 'Subir foto',
  'Remove photo': 'Eliminar foto',
  'Avatar preview': 'Vista previa del avatar',
  Zoom: 'Zoom',
  Horizontal: 'Horizontal',
  Vertical: 'Vertical',
  'We center-crop to a circle using your selected zoom and offsets.':
    'Recortamos al centro en circulo usando el zoom y desplazamientos seleccionados.',
  'First name': 'Nombre',
  'Middle name': 'Segundo nombre',
  'Last name': 'Apellido',
  'DOB (YYYY-MM-DD)': 'Fecha de nacimiento (AAAA-MM-DD)',
  Sex: 'Sexo',
  'Gender identity': 'Identidad de genero',
  Phone: 'Telefono',
  Email: 'Correo electronico',
  Address: 'Direccion',
  City: 'Ciudad',
  State: 'Estado',
  'Postal code': 'Codigo postal',
  'Insurance payer': 'Aseguradora',
  'Policy / Member #': 'Poliza / Nro afiliado',
  'Group #': 'Nro de grupo',
  'Emergency contact': 'Contacto de emergencia',
  'Emergency phone': 'Telefono de emergencia',
  'Primary provider': 'Profesional principal',
  Unassigned: 'Sin asignar',
  Provider: 'Profesional',
  Status: 'Estado',
  Allergies: 'Alergias',
  'Comma separated': 'Separado por comas',
  Flags: 'Banderas',
  Notes: 'Notas',
  Cancel: 'Cancelar',
  'Saving...': 'Guardando...',
  'Save Patient': 'Guardar paciente',
  Filters: 'Filtros',
  'All status': 'Todos los estados',
  'All providers': 'Todos los profesionales',
  'All sex': 'Todos los sexos',
  Female: 'Femenino',
  Male: 'Masculino',
  Other: 'Otro',
  Age: 'Edad',
  'Bulk actions': 'Acciones masivas',
  'Message selected': 'Enviar mensaje',
  Export: 'Exportar',
  Archive: 'Archivar',
  Patient: 'Paciente',
  MRN: 'MRN',
  'Age / Sex': 'Edad / Sexo',
  Contact: 'Contacto',
  'Last visit': 'Ultima visita',
  Actions: 'Acciones',
  Chart: 'Historia',
  Call: 'Llamar',
  'No patients match your filters yet.': 'No hay pacientes que coincidan con los filtros.',
  Allergy: 'Alergia',
  'High risk': 'Alto riesgo',
  Page: 'Pagina',
  of: 'de',
  Prev: 'Anterior',
  Next: 'Siguiente',
  Active: 'Activo',
  Inactive: 'Inactivo',
  Deceased: 'Fallecido',
  Archived: 'Archivado',
  Waiting: 'En espera',
  Scheduled: 'Programado',
  Cancelled: 'Cancelado',
  'Failed to load patients': 'No se pudieron cargar los pacientes',
  'Failed to load patient': 'No se pudo cargar el paciente',
  'First name, last name, DOB, and MRN are required': 'Nombre, apellido, fecha de nacimiento y MRN son obligatorios',
  'Failed to save patient': 'No se pudo guardar el paciente',
  'Unable to prepare avatar upload': 'No se pudo preparar la carga del avatar',
  'Avatar upload failed': 'Fallo la carga del avatar',
  'Load failed': 'Fallo la carga',
  'Update failed': 'Fallo la actualizacion',
  'Create failed': 'Fallo la creacion',
  Profile: 'Perfil',
  Coverage: 'Cobertura',
  Clinical: 'Clinico',
  'Patient identity': 'Identidad del paciente',
  'Core identity, provider, and communication details': 'Identidad principal, profesional y datos de contacto',
  'Address details': 'Detalles de direccion',
  'Searchable address and structured location fields': 'Direccion de busqueda y campos de ubicacion estructurados',
  'Coverage & safety': 'Cobertura y seguridad',
  'Insurance, emergency contact, and patient flags': 'Seguro, contacto de emergencia y banderas del paciente',
  'Clinical notes': 'Notas clinicas',
  'Allergies, flags, and internal notes for intake context': 'Alergias, banderas y notas internas para el contexto de admision',
  'Current setup': 'Configuracion actual',
  'Primary provider and lifecycle status': 'Profesional principal y estado del ciclo de vida',
  'MRN pending': 'MRN pendiente',
  'Patient setup': 'Configuracion del paciente',
  'Work through each tab to capture intake details in a cleaner flow.':
    'Complete cada pestana para capturar la admision con un flujo mas claro.',
  'Photo & preview': 'Foto y vista previa',
  'N/A': 'N/D',
};

const patientItOverrides: Record<string, string> = {
  Patients: 'Pazienti',
  'List of Patients': 'Elenco pazienti',
  'Search patients by name, MRN, phone, email': 'Cerca pazienti per nome, MRN, telefono o email',
  'New Patient': 'Nuovo paziente',
  Hide: 'Nascondi',
  Show: 'Mostra',
  filters: 'filtri',
  'Sort: Name': 'Ordina: nome',
  'Sort: MRN': 'Ordina: MRN',
  'Sort: Last visit': 'Ordina: ultima visita',
  'Sort: Created': 'Ordina: creato',
  Error: 'Errore',
  'Loading patients...': 'Caricamento pazienti...',
  'Edit Patient': 'Modifica paziente',
  'Create Patient': 'Crea paziente',
  'Prefill mock data': 'Compila dati demo',
  Close: 'Chiudi',
  'Loading patient details...': 'Caricamento dettagli paziente...',
  'Patient avatar': 'Avatar paziente',
  'Upload photo': 'Carica foto',
  'Remove photo': 'Rimuovi foto',
  'Avatar preview': 'Anteprima avatar',
  Zoom: 'Zoom',
  Horizontal: 'Orizzontale',
  Vertical: 'Verticale',
  'We center-crop to a circle using your selected zoom and offsets.':
    'Ritagliamo al centro in un cerchio usando zoom e spostamenti selezionati.',
  'First name': 'Nome',
  'Middle name': 'Secondo nome',
  'Last name': 'Cognome',
  'DOB (YYYY-MM-DD)': 'Data di nascita (AAAA-MM-GG)',
  Sex: 'Sesso',
  'Gender identity': 'Identità di genere',
  Phone: 'Telefono',
  Email: 'Email',
  Address: 'Indirizzo',
  'Address Line 1': 'Indirizzo riga 1',
  'Address Line 2': 'Indirizzo riga 2',
  City: 'Città',
  State: 'Provincia',
  'Postal code': 'CAP',
  'Insurance payer': 'Assicurazione',
  'Policy / Member #': 'Polizza / N. iscritto',
  'Group #': 'N. gruppo',
  'Emergency contact': 'Contatto di emergenza',
  'Emergency phone': 'Telefono di emergenza',
  'Primary provider': 'Medico principale',
  Unassigned: 'Non assegnato',
  Provider: 'Medico',
  Status: 'Stato',
  Allergies: 'Allergie',
  'Comma separated': 'Separati da virgola',
  Flags: 'Indicatori',
  Notes: 'Note',
  Cancel: 'Annulla',
  'Saving...': 'Salvataggio...',
  'Save Patient': 'Salva paziente',
  Filters: 'Filtri',
  'All status': 'Tutti gli stati',
  'All providers': 'Tutti i medici',
  'All sex': 'Tutti i sessi',
  Female: 'Femmina',
  Male: 'Maschio',
  Other: 'Altro',
  Age: 'Età',
  'Bulk actions': 'Azioni di massa',
  'Message selected': 'Messaggia selezionati',
  Export: 'Esporta',
  Archive: 'Archivia',
  Patient: 'Paziente',
  MRN: 'MRN',
  'Age / Sex': 'Età / Sesso',
  Contact: 'Contatto',
  'Last visit': 'Ultima visita',
  Actions: 'Azioni',
  Chart: 'Cartella',
  Call: 'Chiama',
  'No patients match your filters yet.': 'Nessun paziente corrisponde ancora ai filtri.',
  Allergy: 'Allergia',
  'High risk': 'Alto rischio',
  Page: 'Pagina',
  of: 'di',
  Prev: 'Prec',
  Next: 'Succ',
  Active: 'Attivo',
  Inactive: 'Inattivo',
  Deceased: 'Deceduto',
  Archived: 'Archiviato',
  Waiting: 'In attesa',
  Scheduled: 'Programmato',
  Cancelled: 'Annullato',
  'Failed to load patients': 'Impossibile caricare i pazienti',
  'Failed to load patient': 'Impossibile caricare il paziente',
  'First name, last name, DOB, and MRN are required': 'Nome, cognome, data di nascita e MRN sono obbligatori',
  'Failed to save patient': 'Impossibile salvare il paziente',
  'Unable to prepare avatar upload': 'Impossibile preparare il caricamento avatar',
  'Avatar upload failed': 'Caricamento avatar non riuscito',
  'Load failed': 'Caricamento non riuscito',
  'Update failed': 'Aggiornamento non riuscito',
  'Create failed': 'Creazione non riuscita',
  Profile: 'Profilo',
  Coverage: 'Copertura',
  Clinical: 'Clinico',
  'Patient identity': 'Identita del paziente',
  'Core identity, provider, and communication details': 'Identita principale, medico e dettagli di contatto',
  'Address details': 'Dettagli indirizzo',
  'Searchable address and structured location fields': 'Indirizzo ricercabile e campi di posizione strutturati',
  'Coverage & safety': 'Copertura e sicurezza',
  'Insurance, emergency contact, and patient flags': 'Assicurazione, contatto di emergenza e indicatori del paziente',
  'Clinical notes': 'Note cliniche',
  'Allergies, flags, and internal notes for intake context': 'Allergie, indicatori e note interne per il contesto di presa in carico',
  'Current setup': 'Configurazione attuale',
  'Primary provider and lifecycle status': 'Medico principale e stato del ciclo di vita',
  'MRN pending': 'MRN in attesa',
  'Patient setup': 'Configurazione paziente',
  'Work through each tab to capture intake details in a cleaner flow.':
    'Compila ogni scheda per registrare i dati con un flusso piu ordinato.',
  'Photo & preview': 'Foto e anteprima',
  'N/A': 'N/D',
};

function getLocale(language: SupportedLanguage) {
  return localeByLanguage[language] ?? 'en-US';
}

function createPatientsTranslator(language: SupportedLanguage, tr: PhraseTranslator): PhraseTranslator {
  if (language === 'en') return tr;

  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;

    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) {
      return phrase.replace(normalized, globalTranslation);
    }

    if (language === 'es' || language === 'it') {
      const direct = language === 'it' ? patientItOverrides[normalized] : patientEsOverrides[normalized];
      if (direct) return phrase.replace(normalized, direct);

      const failedMatch = normalized.match(/^(Load|Update|Create) failed (\d+)$/i);
      if (failedMatch) {
        const verb = failedMatch[1];
        const statusCode = failedMatch[2];
        const translatedVerb =
          verb.toLowerCase() === 'load'
            ? (language === 'it' ? patientItOverrides['Load failed'] : patientEsOverrides['Load failed'])
            : verb.toLowerCase() === 'update'
              ? (language === 'it' ? patientItOverrides['Update failed'] : patientEsOverrides['Update failed'])
              : (language === 'it' ? patientItOverrides['Create failed'] : patientEsOverrides['Create failed']);
        return phrase.replace(normalized, `${translatedVerb} ${statusCode}`);
      }
    }

    return phrase;
  };
}

function localizePatientStatus(status: PatientStatus, translate: PhraseTranslator) {
  return translate(statusLabels[status]);
}

function formatPatientDateTime(value: string, locale: string, translate: PhraseTranslator) {
  if (!value) return translate('N/A');
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
}

export default function Patients() {
  const { language, tr } = useI18n();
  const locale = getLocale(language);
  const tp = useMemo(() => createPatientsTranslator(language, tr), [language, tr]);
  const navigate = useNavigate();
  const { fetchApi } = useApiClient();
  const [patients, setPatients] = useState<PatientRow[]>([]);
  const [editingPatient, setEditingPatient] = useState<PatientRow | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PatientStatus | 'All'>('All');
  const [providerFilter, setProviderFilter] = useState<string>('All');
  const [providerOptions, setProviderOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [sexFilter, setSexFilter] = useState<'All' | Sex>('All');
  const [ageRange, setAgeRange] = useState<[number, number]>([0, 120]);
  const [sortBy, setSortBy] = useState<'name' | 'mrn' | 'lastVisit' | 'created'>('name');
  const [clientViews, setClientViews] = useState<ClientView[]>([]);
  const [activeClientViewId, setActiveClientViewId] = useState('default');
  const [bulkAction, setBulkAction] = useState<string>('none');
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 15;
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<PatientFormState>(() => emptyFormState());
  const [activeModalTab, setActiveModalTab] = useState<PatientModalTab>('profile');
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);
  const [avatarImage, setAvatarImage] = useState<HTMLImageElement | null>(null);
  const [avatarScale, setAvatarScale] = useState(1);
  const [avatarOffset, setAvatarOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [showCreateFieldSettings, setShowCreateFieldSettings] = useState(false);
  const [createFieldSettings, setCreateFieldSettings] = useState(defaultCreateFieldSettings);
  const [viewSaving, setViewSaving] = useState(false);
  const [lifecycleBusyId, setLifecycleBusyId] = useState<string | null>(null);
  const [mergeSource, setMergeSource] = useState<PatientRow | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState('');

  const mapClientViewFromApi = useCallback((row: any): ClientView => {
    const filters = row.filters || {};
    return {
      id: row.id,
      name: row.name,
      statusFilter: filters.statusFilter || 'All',
      providerFilter: filters.providerFilter || 'All',
      sexFilter: filters.sexFilter || 'All',
      sortBy: row.sortBy === 'createdAt' ? 'created' : row.sortBy || filters.sortBy || 'name',
      isDefault: Boolean(row.isDefault),
    };
  }, []);

  const loadClientViews = useCallback(async () => {
    try {
      const res = await fetchApi('/patients/views');
      if (!res.ok) throw new Error(`Views load failed ${res.status}`);
      const rows = await res.json();
      const mapped = rows.map(mapClientViewFromApi);
      setClientViews(mapped);
      const defaultView = mapped.find((view: ClientView) => view.isDefault);
      if (defaultView && activeClientViewId === 'default') {
        setActiveClientViewId(defaultView.id);
        setStatusFilter(defaultView.statusFilter);
        setProviderFilter(defaultView.providerFilter);
        setSexFilter(defaultView.sexFilter);
        setSortBy(defaultView.sortBy);
      }
    } catch {
      setClientViews([]);
    }
  }, [activeClientViewId, fetchApi, mapClientViewFromApi]);

  const loadPatients = useCallback(async () => {


    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (search) params.append('q', search);
    if (statusFilter !== 'All') params.append('status', statusFilter);
    if (providerFilter !== 'All') params.append('providerId', providerFilter);
    if (sexFilter !== 'All') params.append('sex', sexFilter);
    params.append('sortBy', sortBy === 'created' ? 'createdAt' : sortBy);
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));
    try {
      const res = await fetchApi(`/patients?${params.toString()}`);
      if (!res.ok) throw new Error(`Load failed ${res.status}`);
      const data = await res.json();
      const mapped = data.data.map((p: any) => mapPatientFromApi(p));
      setPatients(mapped);
      const providerMap = new Map<string, string>();
      data.data.forEach((p: any) => {
        if (p.primaryProvider?.id) {
          providerMap.set(p.primaryProvider.id, p.primaryProvider.name ?? '');
        }
      });
      const providerList = Array.from(providerMap.entries()).map(([id, name]) => ({ id, name }));
      setProviderOptions(providerList);
      if (providerFilter !== 'All' && providerList.every((p) => p.id !== providerFilter)) {
        setProviderFilter('All');
      }
      setTotal(data.total);
    } catch (err: any) {
      setError(tp(err.message || 'Failed to load patients'));
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, providerFilter, sexFilter, sortBy, page, fetchApi, tp]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  useEffect(() => {
    void loadClientViews();
  }, [loadClientViews]);

  const filtered = useMemo(() => {
    let rows = patients;
    if (statusFilter !== 'All') rows = rows.filter((p) => p.status === statusFilter);
    if (providerFilter !== 'All') rows = rows.filter((p) => p.primaryProviderId === providerFilter);
    if (sexFilter !== 'All') rows = rows.filter((p) => p.sex === sexFilter);
    rows = rows.filter((p) => p.age >= ageRange[0] && p.age <= ageRange[1]);
    const sorted = [...rows].sort((a, b) => {
      if (sortBy === 'mrn') return a.mrn.localeCompare(b.mrn);
      if (sortBy === 'lastVisit') return (b.lastVisit ?? '').localeCompare(a.lastVisit ?? '');
      return `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`);
    });
    return sorted;
  }, [patients, statusFilter, providerFilter, sexFilter, ageRange, sortBy]);

  function resetPatientModal() {
    setShowModal(false);
    setEditingPatient(null);
    setModalError(null);
    setModalLoading(false);
    setActiveModalTab('profile');
    setForm(emptyFormState());
    setAvatarSrc(null);
    setAvatarImage(null);
    setAvatarScale(1);
    setAvatarOffset({ x: 0, y: 0 });
  }

  function openCreateModal() {
    setEditingPatient(null);
    setModalError(null);
    setActiveModalTab('profile');
    setForm(emptyFormState());
    setAvatarSrc(null);
    setAvatarImage(null);
    setAvatarScale(1);
    setAvatarOffset({ x: 0, y: 0 });
    setShowModal(true);
  }

  async function saveClientView() {
    const name = search.trim() ? `${search.trim()} view` : statusFilter !== 'All' ? `${statusFilter} clients` : `Client view ${clientViews.length + 1}`;
    setViewSaving(true);
    setError(null);
    try {
      const res = await fetchApi('/patients/views', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          filters: { statusFilter, providerFilter, sexFilter },
          sortBy: sortBy === 'created' ? 'createdAt' : sortBy,
          sortOrder: 'asc',
        }),
      });
      if (!res.ok) throw new Error(`Save view failed ${res.status}`);
      const created = mapClientViewFromApi(await res.json());
      setClientViews((current) => [created, ...current]);
      setActiveClientViewId(created.id);
    } catch (err: any) {
      setError(tp(err.message || 'Failed to save client view'));
    } finally {
      setViewSaving(false);
    }
  }

  function applyClientView(viewId: string) {
    setActiveClientViewId(viewId);
    setPage(1);
    if (viewId === 'default') {
      setStatusFilter('All');
      setProviderFilter('All');
      setSexFilter('All');
      setSortBy('name');
      return;
    }
    if (viewId === 'inactive') {
      setStatusFilter('INACTIVE');
      setProviderFilter('All');
      setSexFilter('All');
      setSortBy('lastVisit');
      return;
    }
    const view = clientViews.find((item) => item.id === viewId);
    if (!view) return;
    setStatusFilter(view.statusFilter);
    setProviderFilter(view.providerFilter);
    setSexFilter(view.sexFilter);
    setSortBy(view.sortBy);
  }

  async function deactivatePatient(patient: PatientRow) {
    if (!window.confirm(tp('Deactivate this client?'))) return;
    setLifecycleBusyId(patient.id);
    setError(null);
    try {
      const res = await fetchApi(`/patients/${patient.id}/deactivate`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      if (!res.ok) throw new Error(`Deactivate failed ${res.status}`);
      await loadPatients();
    } catch (err: any) {
      setError(tp(err.message || 'Failed to deactivate client'));
    } finally {
      setLifecycleBusyId(null);
    }
  }

  async function reactivatePatient(patient: PatientRow) {
    setLifecycleBusyId(patient.id);
    setError(null);
    try {
      const res = await fetchApi(`/patients/${patient.id}/reactivate`, { method: 'PATCH' });
      if (!res.ok) throw new Error(`Reactivate failed ${res.status}`);
      await loadPatients();
    } catch (err: any) {
      setError(tp(err.message || 'Failed to reactivate client'));
    } finally {
      setLifecycleBusyId(null);
    }
  }

  async function deletePatient(patient: PatientRow) {
    if (!window.confirm(tp('Permanently delete this client?'))) return;
    setLifecycleBusyId(patient.id);
    setError(null);
    try {
      const res = await fetchApi(`/patients/${patient.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Delete failed ${res.status}`);
      }
      await loadPatients();
    } catch (err: any) {
      setError(tp(err.message || 'Failed to delete client'));
    } finally {
      setLifecycleBusyId(null);
    }
  }

  async function mergeClients() {
    if (!mergeSource || !mergeTargetId) return;
    setLifecycleBusyId(mergeSource.id);
    setError(null);
    try {
      const res = await fetchApi('/patients/merge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourcePatientId: mergeSource.id, targetPatientId: mergeTargetId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Merge failed ${res.status}`);
      }
      setMergeSource(null);
      setMergeTargetId('');
      await loadPatients();
    } catch (err: any) {
      setError(tp(err.message || 'Failed to merge clients'));
    } finally {
      setLifecycleBusyId(null);
    }
  }

  const prefillMockData = () => {
    const now = Date.now();
    setForm((prev) => ({
      ...prev,
      mrn: prev.mrn || `MRN-${now}`,
      firstName: 'Jane',
      middleName: 'Q',
      lastName: 'Tester',
      dob: '1990-05-10',
      sex: 'F',
      genderIdentity: 'Female',
      phone: '555-212-0000',
      email: 'jane.tester@example.com',
      addressLine1: '123 Mockingbird Ln',
      addressLine2: 'Apt 4B',
      city: 'New York',
      state: 'NY',
      postalCode: '10001',
      country: 'US',
      formattedAddress: '123 Mockingbird Ln, Apt 4B, New York, NY 10001, US',
      googlePlaceId: '',
      latitude: '',
      longitude: '',
      source: 'MANUAL',
      insuranceProvider: 'Acme Health',
      insuranceMemberId: 'MEM-12345',
      insuranceGroupNumber: 'GRP-6789',
      emergencyName: 'Sam Tester',
      emergencyPhone: '555-111-2222',
      clinicalNotes: 'Prefilled mock data for quick testing',
      status: 'ACTIVE',
      allergies: 'Penicillin - rash, Tree nuts',
      flags: ['allergy'],
    }));
  };

  async function startEditPatient(p: PatientRow) {
    setModalError(null);
    setEditingPatient(p);
    setActiveModalTab('profile');
    setShowModal(true);
    setModalLoading(true);
    try {
      const res = await fetchApi(`/patients/${p.id}`);
      if (!res.ok) throw new Error(`Load failed ${res.status}`);
      const detail = await res.json();
      setForm({
        firstName: detail.firstName ?? '',
        middleName: detail.middleName ?? '',
        lastName: detail.lastName ?? '',
        dob: detail.dob ? detail.dob.slice(0, 10) : '',
        sex: detail.sex ?? 'O',
        genderIdentity: detail.genderIdentity ?? '',
        mrn: detail.mrn ?? '',
        phone: detail.phone ?? '',
        email: detail.email ?? '',
        addressLine1: detail.addressLine1 ?? detail.address?.line1 ?? '',
        addressLine2: detail.addressLine2 ?? detail.address?.line2 ?? '',
        city: detail.city ?? '',
        state: detail.state ?? '',
        postalCode: detail.postalCode ?? '',
        country: detail.country ?? detail.address?.country ?? 'US',
        address: detail.addressText ?? detail.address ?? '',
        formattedAddress: detail.formattedAddress ?? detail.address?.formattedAddress ?? '',
        googlePlaceId: detail.googlePlaceId ?? detail.address?.googlePlaceId ?? '',
        latitude: detail.latitude != null ? String(detail.latitude) : '',
        longitude: detail.longitude != null ? String(detail.longitude) : '',
        source: detail.source ?? detail.address?.source ?? 'MANUAL',
        insuranceProvider: detail.insuranceProvider ?? '',
        insuranceMemberId: detail.insuranceMemberId ?? '',
        insuranceGroupNumber: detail.insuranceGroupNumber ?? '',
        emergencyName: detail.emergencyContactName ?? '',
        emergencyPhone: detail.emergencyContactPhone ?? '',
        clinicalNotes: detail.clinicalNotes ?? '',
        primaryProviderId: detail.primaryProvider?.id ?? '',
        preferredPharmacyId: detail.preferredPharmacyId ?? '',
        avatarUrl: detail.avatarUrl ?? detail.avatar ?? '',
        avatarData: detail.avatarUrl ?? detail.avatar ?? '',
        status: (detail.status as PatientStatus) ?? 'ACTIVE',
        allergies: formatAllergies(detail.allergies).join(', '),
        flags: detail.flags ?? [],
      });
      if (detail.avatarUrl || detail.avatar) {
        const src = detail.avatarUrl || detail.avatar;
        setAvatarSrc(src);
        const img = new Image();
        img.onload = () => {
          setAvatarImage(img);
          setAvatarScale(1);
          setAvatarOffset({ x: 0, y: 0 });
          regenerateAvatar(img, 1, { x: 0, y: 0 });
        };
        img.onerror = () => {
          setAvatarSrc(null);
          setAvatarImage(null);
          setAvatarScale(1);
          setAvatarOffset({ x: 0, y: 0 });
          setForm((prev) => ({ ...prev, avatarData: '', avatarUrl: '' }));
        };
        img.src = src;
      } else {
        setAvatarSrc(null);
        setAvatarImage(null);
        setAvatarScale(1);
        setAvatarOffset({ x: 0, y: 0 });
      }
    } catch (err: any) {
      setModalError(tp(err.message || 'Failed to load patient'));
    } finally {
      setModalLoading(false);
    }
  }

  async function savePatient() {
    setSaving(true);
    setModalError(null);
    try {
      if (!form.firstName || !form.lastName || !form.mrn || !form.dob) {
        throw new Error(tp('First name, last name, DOB, and MRN are required'));
      }
      const missingConfiguredFields = configurableClientFields
        .filter((field) => createFieldSettings[field.id].visible && createFieldSettings[field.id].required)
        .filter((field) => {
          if (field.id === 'primaryProvider') return !form.primaryProviderId;
          if (field.id === 'insurance') return !form.insuranceProvider || !form.insuranceMemberId;
          if (field.id === 'emergencyContact') return !form.emergencyName || !form.emergencyPhone;
          if (field.id === 'clinicalNotes') return !form.clinicalNotes.trim();
          return !String(form[field.id as keyof PatientFormState] || '').trim();
        })
        .map((field) => tp(field.label));
      if (missingConfiguredFields.length) {
        throw new Error(`${tp('Missing required fields')}: ${missingConfiguredFields.join(', ')}`);
      }
      const avatarUrl = await uploadAvatarIfNeeded(editingPatient?.id);
      const allergies = form.allergies
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean);
      const payload = {
        mrn: form.mrn,
        firstName: form.firstName,
        middleName: form.middleName || undefined,
        lastName: form.lastName,
        dob: form.dob,
        sex: form.sex,
        genderIdentity: form.genderIdentity || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        status: form.status,
        addressLine1: form.addressLine1 || undefined,
        addressLine2: form.addressLine2 || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        postalCode: form.postalCode || undefined,
        country: form.country || undefined,
        formattedAddress: form.formattedAddress || undefined,
        googlePlaceId: form.googlePlaceId || undefined,
        latitude: form.latitude ? Number(form.latitude) : undefined,
        longitude: form.longitude ? Number(form.longitude) : undefined,
        source: form.source || undefined,
        insuranceProvider: form.insuranceProvider || undefined,
        insuranceMemberId: form.insuranceMemberId || undefined,
        insuranceGroupNumber: form.insuranceGroupNumber || undefined,
        emergencyContactName: form.emergencyName || undefined,
        emergencyContactPhone: form.emergencyPhone || undefined,
        preferredLanguage: undefined,
        primaryProviderId: form.primaryProviderId || undefined,
        lastVisitAt: undefined,
        flags: form.flags,
        tags: undefined,
        allergies,
        problems: [],
        medications: [],
        pastMedicalHistory: [],
        surgeries: [],
        familyHistory: [],
        socialHistory: [],
        avatarUrl: avatarUrl || undefined,
      };
      const method = editingPatient ? 'PUT' : 'POST';
      const url = editingPatient ? `/patients/${editingPatient.id}` : '/patients';
      const res = await fetchApi(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`${editingPatient ? 'Update' : 'Create'} failed ${res.status}`);
      await loadPatients();
      resetPatientModal();
    } catch (err: any) {
      setModalError(tp(err.message || 'Failed to save patient'));
    } finally {
      setSaving(false);
    }
  }

  const patientDisplayName = `${form.firstName} ${form.lastName}`.trim();
  const selectedProviderName = providerOptions.find((provider) => provider.id === form.primaryProviderId)?.name || tp('Unassigned');
  const modalTabs: Array<{ id: PatientModalTab; label: string }> = [
    { id: 'profile', label: tp('Profile') },
    { id: 'address', label: tp('Address') },
    { id: 'coverage', label: tp('Coverage') },
    { id: 'clinical', label: tp('Clinical') },
  ];

  function toggleFlag(flag: PatientRow['flags'][number]) {
    setForm((prev) => {
      const exists = prev.flags.includes(flag);
      return { ...prev, flags: exists ? prev.flags.filter((f) => f !== flag) : [...prev.flags, flag] };
    });
  }

  function toggleCreateField(fieldId: ClientCreateFieldId, key: 'visible' | 'required') {
    setCreateFieldSettings((current) => ({
      ...current,
      [fieldId]: {
        ...current[fieldId],
        [key]: !current[fieldId][key],
        required: key === 'visible' && current[fieldId].visible ? false : key === 'required' ? !current[fieldId].required : current[fieldId].required,
      },
    }));
  }

  function regenerateAvatar(img: HTMLImageElement, scale: number, offset: { x: number; y: number }) {
    const canvas = document.createElement('canvas');
    const size = AVATAR_DIMENSION_PX;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, size, size);
    ctx.save();
    ctx.translate(size / 2 + offset.x, size / 2 + offset.y);
    ctx.scale(scale, scale);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    ctx.restore();
    const dataUrl = canvas.toDataURL('image/jpeg', AVATAR_JPEG_QUALITY);
    setForm((prev) => ({ ...prev, avatarData: dataUrl }));
  }

  function handleAvatarFile(file?: File) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result as string;
      const img = new Image();
      img.onload = () => {
        setAvatarSrc(src);
        setAvatarImage(img);
        setAvatarScale(1);
        setAvatarOffset({ x: 0, y: 0 });
        regenerateAvatar(img, 1, { x: 0, y: 0 });
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  }

  async function uploadAvatarIfNeeded(patientId?: string): Promise<string | undefined> {
    if (!avatarImage) return form.avatarUrl || undefined;
    const blob = await generateAvatarBlob();
    if (!blob) return form.avatarUrl || undefined;
    const presignRes = await fetchApi('/storage/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: 'avatar.jpg',
        contentType: blob.type || 'image/jpeg',
        kind: 'patient-avatar',
        patientId,
      }),
    });
    if (!presignRes.ok) throw new Error(tp('Unable to prepare avatar upload'));
    const { uploadUrl, url } = await presignRes.json();
    const putRes = await fetch(uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': blob.type } });
    if (!putRes.ok) throw new Error(tp('Avatar upload failed'));
    return url as string;
  }

  async function generateAvatarBlob(): Promise<Blob | null> {
    if (!avatarImage) return null;
    return await renderAvatarBlob((ctx, size) => {
      ctx.save();
      ctx.translate(size / 2 + avatarOffset.x, size / 2 + avatarOffset.y);
      ctx.scale(avatarScale, avatarScale);
      ctx.drawImage(avatarImage, -avatarImage.width / 2, -avatarImage.height / 2);
      ctx.restore();
    });
  }

  useEffect(() => {
    if (avatarImage) {
      regenerateAvatar(avatarImage, avatarScale, avatarOffset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarScale, avatarOffset]);
  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{tp('Patients')}</p>
            <h1 className="mt-1 text-2xl font-semibold text-ink">{tp('List of Patients')}</h1>
            <p className="max-w-3xl text-sm text-slate-600">
              {tp('Search patients by name, MRN, phone, email')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-full min-w-[260px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 pl-9 text-sm outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100"
                placeholder={tp('Search patients by name, MRN, phone, email')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              className="inline-flex min-w-[160px] items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-lime-400 px-5 py-2 text-sm font-semibold text-slate-900 hover:bg-lime-300"
              onClick={openCreateModal}
            >
              <Plus size={16} /> {tp('New Patient')}
            </button>
          </div>
        </div>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-4">
          <div className="flex items-center gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setShowFilters((v) => !v)}
            >
              <Filter size={16} /> {showFilters ? tp('Hide') : tp('Show')} {tp('filters')}
            </button>
            <select
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm bg-white"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
            >
              <option value="name">{tp('Sort: Name')}</option>
              <option value="mrn">{tp('Sort: MRN')}</option>
              <option value="lastVisit">{tp('Sort: Last visit')}</option>
              <option value="created">{tp('Sort: Created')}</option>
            </select>
            <select
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
              value={activeClientViewId}
              onChange={(event) => applyClientView(event.target.value)}
            >
              <option value="default">{tp('Default View')}</option>
              <option value="inactive">{tp('Inactive Clients')}</option>
              {clientViews.map((view) => (
                <option key={view.id} value={view.id}>{view.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={saveClientView}
              disabled={viewSaving}
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {viewSaving ? tp('Saving...') : tp('Save View')}
            </button>
            <button
              type="button"
              onClick={() => applyClientView('inactive')}
              className={cn(
                'rounded-xl border px-3 py-2 text-sm font-medium',
                statusFilter === 'INACTIVE' ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
              )}
            >
              {tp('Inactive Clients')}
            </button>
          </div>
          <Pagination
            current={page}
            total={Math.max(1, Math.ceil(total / pageSize))}
            onPageChange={(p) => setPage(p)}
            translate={tp}
          />
        </div>

        <div className="p-4 space-y-4">
          {showFilters && (
            <Toolbar
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              providerFilter={providerFilter}
              setProviderFilter={setProviderFilter}
              providerOptions={providerOptions}
              sexFilter={sexFilter}
              setSexFilter={setSexFilter}
              ageRange={ageRange}
              setAgeRange={setAgeRange}
              sortBy={sortBy}
              setSortBy={setSortBy}
              bulkAction={bulkAction}
              setBulkAction={setBulkAction}
              translate={tp}
            />
          )}

          {error ? <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{tp('Error')}: {tp(error)}</div> : null}
          {loading ? (
            <div className="p-4 text-sm text-slate-500">{tp('Loading patients...')}</div>
          ) : (
            <PatientTable
              patients={filtered}
              onSelect={(id) => navigate(`/patients/${id}`)}
              onEdit={startEditPatient}
              onDeactivate={deactivatePatient}
              onReactivate={reactivatePatient}
              onDelete={deletePatient}
              onMerge={(patient) => {
                setMergeSource(patient);
                setMergeTargetId('');
              }}
              onReferral={(patient) => navigate(`/patients/${patient.id}?tab=Coordination`)}
              busyId={lifecycleBusyId}
              translate={tp}
              locale={locale}
            />
          )}
        </div>
      </section>

      {mergeSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-ink">{tp('Merge Clients')}</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {tp('Move records from')} {mergeSource.firstName} {mergeSource.lastName} {tp('into another client profile.')}
                </p>
              </div>
              <button
                type="button"
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-ink"
                onClick={() => {
                  setMergeSource(null);
                  setMergeTargetId('');
                }}
                aria-label={tp('Close')}
              >
                <X size={18} />
              </button>
            </div>
            <label className="mt-4 block text-sm font-medium text-slate-700">{tp('Target client')}</label>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm"
              value={mergeTargetId}
              onChange={(event) => setMergeTargetId(event.target.value)}
            >
              <option value="">{tp('Select client')}</option>
              {patients
                .filter((patient) => patient.id !== mergeSource.id)
                .map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.firstName} {patient.lastName} - {patient.mrn}
                  </option>
                ))}
            </select>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  setMergeSource(null);
                  setMergeTargetId('');
                }}
              >
                {tp('Cancel')}
              </button>
              <button
                type="button"
                className="rounded-xl bg-ink px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                onClick={mergeClients}
                disabled={!mergeTargetId || lifecycleBusyId === mergeSource.id}
              >
                {lifecycleBusyId === mergeSource.id ? tp('Saving...') : tp('Merge Clients')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-start justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-semibold text-ink">{editingPatient ? tp('Edit Patient') : tp('Create Patient')}</h3>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{tp('Patient setup')}</span>
                </div>
                <p className="text-sm text-slate-500">{tp('Work through each tab to capture intake details in a cleaner flow.')}</p>
                {modalError && <p className="text-sm text-rose-600">{tp(modalError)}</p>}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:text-ink"
                  type="button"
                  onClick={prefillMockData}
                  disabled={modalLoading || saving}
                >
                  {tp('Prefill mock data')}
                </button>
                <button
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-ink"
                  onClick={resetPatientModal}
                  aria-label={tp('Close')}
                  type="button"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-slate-50/40">
              <div className="space-y-6 p-6">
                {modalLoading ? (
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
                    {tp('Loading patient details...')}
                  </div>
                ) : (
                  <>
                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(320px,1fr)]">
                        <div className="flex flex-col gap-4 md:flex-row md:items-center">
                          <InitialAvatar
                            name={patientDisplayName || `${form.firstName} ${form.lastName}`}
                            src={form.avatarData || avatarSrc}
                            alt={tp('Patient avatar')}
                            className="h-24 w-24 text-xl"
                          />
                          <div className="min-w-0 flex-1 space-y-3">
                            <div>
                              <p className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">{tp('Current setup')}</p>
                              <h4 className="truncate text-2xl font-semibold text-ink">{patientDisplayName || tp('Create Patient')}</h4>
                              <p className="text-sm text-slate-500">{form.mrn || tp('MRN pending')}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                                {tp('Primary provider')}: {selectedProviderName}
                              </span>
                              <span className={cn('pill', statusColors[form.status])}>{localizePatientStatus(form.status, tp)}</span>
                            </div>
                            <p className="text-sm text-slate-500">{tp('Primary provider and lifecycle status')}</p>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-ink">{tp('Photo & preview')}</p>
                              <p className="text-xs text-slate-500">{tp('We center-crop to a circle using your selected zoom and offsets.')}</p>
                            </div>
                            {(form.avatarData || avatarSrc) && (
                              <button
                                type="button"
                                className="flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600"
                                onClick={() => {
                                  setAvatarSrc(null);
                                  setAvatarImage(null);
                                  setAvatarScale(1);
                                  setAvatarOffset({ x: 0, y: 0 });
                                  setForm((prev) => ({ ...prev, avatarData: '', avatarUrl: '' }));
                                }}
                              >
                                <X size={12} /> {tp('Remove photo')}
                              </button>
                            )}
                          </div>
                          <div className="grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)]">
                            <div className="flex flex-col items-center gap-3">
                              <InitialAvatar
                                name={patientDisplayName || `${form.firstName} ${form.lastName}`}
                                src={form.avatarData || avatarSrc}
                                alt={tp('Avatar preview')}
                                className="h-32 w-32 text-2xl"
                              />
                              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-700 hover:border-ink">
                                <Upload size={16} />
                                <span>{tp('Upload photo')}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleAvatarFile(e.target.files?.[0])}
                                />
                              </label>
                            </div>
                            {avatarSrc ? (
                              <div className="space-y-3">
                                <div className="relative h-40 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                                  <div className="absolute inset-3 overflow-hidden rounded-full">
                                    <img
                                      src={avatarSrc}
                                      alt={tp('Avatar preview')}
                                      className="absolute left-1/2 top-1/2 h-full w-full object-cover"
                                      onError={() => {
                                        setAvatarSrc(null);
                                        setAvatarImage(null);
                                        setAvatarScale(1);
                                        setAvatarOffset({ x: 0, y: 0 });
                                        setForm((prev) => ({ ...prev, avatarData: '', avatarUrl: '' }));
                                      }}
                                      style={{
                                        transform: `translate(-50%, -50%) translate(${avatarOffset.x}px, ${avatarOffset.y}px) scale(${avatarScale})`,
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="grid gap-2 text-xs text-slate-600">
                                  <label className="flex items-center gap-3">
                                    <span className="w-20">{tp('Zoom')}</span>
                                    <input
                                      type="range"
                                      min={1}
                                      max={2}
                                      step={0.05}
                                      value={avatarScale}
                                      onChange={(e) => setAvatarScale(Number(e.target.value))}
                                      className="flex-1 accent-ink"
                                    />
                                  </label>
                                  <label className="flex items-center gap-3">
                                    <span className="w-20">{tp('Horizontal')}</span>
                                    <input
                                      type="range"
                                      min={-60}
                                      max={60}
                                      step={1}
                                      value={avatarOffset.x}
                                      onChange={(e) => setAvatarOffset((prev) => ({ ...prev, x: Number(e.target.value) }))}
                                      className="flex-1 accent-ink"
                                    />
                                  </label>
                                  <label className="flex items-center gap-3">
                                    <span className="w-20">{tp('Vertical')}</span>
                                    <input
                                      type="range"
                                      min={-60}
                                      max={60}
                                      step={1}
                                      value={avatarOffset.y}
                                      onChange={(e) => setAvatarOffset((prev) => ({ ...prev, y: Number(e.target.value) }))}
                                      className="flex-1 accent-ink"
                                    />
                                  </label>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                                {tp('Upload photo')}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap gap-2">
                        {modalTabs.map((tab) => (
                          <ModalTabButton
                            key={tab.id}
                            label={tab.label}
                            active={activeModalTab === tab.id}
                            onClick={() => setActiveModalTab(tab.id)}
                          />
                        ))}
                        </div>
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          onClick={() => setShowCreateFieldSettings((value) => !value)}
                        >
                          <Settings size={15} /> {tp('Customize create screen')}
                        </button>
                      </div>
                    </section>

                    {showCreateFieldSettings && (
                      <ModalSection
                        title={tp('Create-client field controls')}
                        subtitle={tp('Choose which intake fields are shown and which are mandatory.')}
                      >
                        <div className="grid gap-2 md:grid-cols-2">
                          {configurableClientFields.map((field) => (
                            <div key={field.id} className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2">
                              <span className="text-sm font-medium text-slate-700">{tp(field.label)}</span>
                              <div className="flex items-center gap-4 text-xs text-slate-600">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={createFieldSettings[field.id].visible}
                                    onChange={() => toggleCreateField(field.id, 'visible')}
                                    className="accent-ink"
                                  />
                                  {tp('Show')}
                                </label>
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={createFieldSettings[field.id].required}
                                    disabled={!createFieldSettings[field.id].visible}
                                    onChange={() => toggleCreateField(field.id, 'required')}
                                    className="accent-ink disabled:opacity-40"
                                  />
                                  {tp('Mandatory')}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ModalSection>
                    )}

                    {activeModalTab === 'profile' && (
                      <>
                        <ModalSection
                          title={tp('Patient identity')}
                          subtitle={tp('Core identity, provider, and communication details')}
                        >
                          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                            <TextField label={tp('First name')} value={form.firstName} onChange={(v) => setForm({ ...form, firstName: v })} />
                            {createFieldSettings.middleName.visible && (
                              <TextField label={`${tp('Middle name')}${createFieldSettings.middleName.required ? ' *' : ''}`} value={form.middleName} onChange={(v) => setForm({ ...form, middleName: v })} />
                            )}
                            <TextField label={tp('Last name')} value={form.lastName} onChange={(v) => setForm({ ...form, lastName: v })} />
                            <TextField label={tp('DOB (YYYY-MM-DD)')} type="date" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} />
                            <label className="space-y-1 text-sm">
                              <span className="text-slate-500">{tp('Sex')}</span>
                              <select
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                                value={form.sex}
                                onChange={(e) => setForm({ ...form, sex: e.target.value })}
                              >
                                <option value="M">{tp('Male')}</option>
                                <option value="F">{tp('Female')}</option>
                                <option value="O">{tp('Other')}</option>
                              </select>
                            </label>
                            {createFieldSettings.genderIdentity.visible && (
                              <TextField
                                label={`${tp('Gender identity')}${createFieldSettings.genderIdentity.required ? ' *' : ''}`}
                                value={form.genderIdentity}
                                onChange={(v) => setForm({ ...form, genderIdentity: v })}
                              />
                            )}
                            <TextField label={tp('MRN')} value={form.mrn} onChange={(v) => setForm({ ...form, mrn: v })} />
                            {createFieldSettings.phone.visible && (
                              <TextField label={`${tp('Phone')}${createFieldSettings.phone.required ? ' *' : ''}`} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                            )}
                            {createFieldSettings.email.visible && (
                              <TextField label={`${tp('Email')}${createFieldSettings.email.required ? ' *' : ''}`} value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                            )}
                          </div>
                        </ModalSection>

                        <ModalSection title={tp('Current setup')} subtitle={tp('Primary provider and lifecycle status')}>
                          <div className="grid gap-4 md:grid-cols-2">
                            {createFieldSettings.primaryProvider.visible && (
                              <label className="space-y-1 text-sm">
                                <span className="text-slate-500">{tp('Primary provider')}{createFieldSettings.primaryProvider.required ? ' *' : ''}</span>
                                <select
                                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                                  value={form.primaryProviderId}
                                  onChange={(e) => setForm({ ...form, primaryProviderId: e.target.value })}
                                >
                                  <option value="">{tp('Unassigned')}</option>
                                  {providerOptions.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.name || tp('Provider')}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            )}
                            <label className="space-y-1 text-sm">
                              <span className="text-slate-500">{tp('Status')}</span>
                              <select
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value as PatientStatus })}
                              >
                                {statuses.map((s) => (
                                  <option key={s} value={s}>
                                    {localizePatientStatus(s, tp)}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                        </ModalSection>
                      </>
                    )}

                    {activeModalTab === 'address' && (
                      <ModalSection
                        title={tp('Address details')}
                        subtitle={tp('Searchable address and structured location fields')}
                      >
                        <AddressFields
                          value={{
                            line1: form.addressLine1,
                            line2: form.addressLine2,
                            city: form.city,
                            state: form.state,
                            postalCode: form.postalCode,
                            country: form.country,
                            formattedAddress: form.formattedAddress,
                            googlePlaceId: form.googlePlaceId,
                            latitude: form.latitude ? Number(form.latitude) : null,
                            longitude: form.longitude ? Number(form.longitude) : null,
                            source: form.source as 'MANUAL' | 'GOOGLE' | 'IMPORTED',
                          }}
                          onChange={(next) =>
                            setForm((prev) => ({
                              ...prev,
                              addressLine1: next.line1,
                              addressLine2: next.line2,
                              city: next.city,
                              state: next.state,
                              postalCode: next.postalCode,
                              country: next.country,
                              formattedAddress: next.formattedAddress ?? '',
                              googlePlaceId: next.googlePlaceId ?? '',
                              latitude: next.latitude != null ? String(next.latitude) : '',
                              longitude: next.longitude != null ? String(next.longitude) : '',
                              source: (next.source ?? 'MANUAL') as 'MANUAL' | 'GOOGLE' | 'IMPORTED',
                            }))
                          }
                          labels={{
                            line1: tp('Address Line 1'),
                            line2: tp('Address Line 2'),
                            city: tp('City'),
                            state: tp('State'),
                            postalCode: tp('Postal code'),
                            country: tp('Country'),
                          }}
                        />
                      </ModalSection>
                    )}

                    {activeModalTab === 'coverage' && (
                      <ModalSection
                        title={tp('Coverage & safety')}
                        subtitle={tp('Insurance, emergency contact, and patient flags')}
                      >
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                          {createFieldSettings.insurance.visible && (
                            <>
                              <TextField
                                label={`${tp('Insurance payer')}${createFieldSettings.insurance.required ? ' *' : ''}`}
                                value={form.insuranceProvider}
                                onChange={(v) => setForm({ ...form, insuranceProvider: v })}
                              />
                              <TextField
                                label={`${tp('Policy / Member #')}${createFieldSettings.insurance.required ? ' *' : ''}`}
                                value={form.insuranceMemberId}
                                onChange={(v) => setForm({ ...form, insuranceMemberId: v })}
                              />
                              <TextField
                                label={tp('Group #')}
                                value={form.insuranceGroupNumber}
                                onChange={(v) => setForm({ ...form, insuranceGroupNumber: v })}
                              />
                            </>
                          )}
                          {createFieldSettings.emergencyContact.visible && (
                            <>
                              <TextField
                                label={`${tp('Emergency contact')}${createFieldSettings.emergencyContact.required ? ' *' : ''}`}
                                value={form.emergencyName}
                                onChange={(v) => setForm({ ...form, emergencyName: v })}
                              />
                              <TextField
                                label={`${tp('Emergency phone')}${createFieldSettings.emergencyContact.required ? ' *' : ''}`}
                                value={form.emergencyPhone}
                                onChange={(v) => setForm({ ...form, emergencyPhone: v })}
                              />
                            </>
                          )}
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <span className="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">{tp('Flags')}</span>
                          <FlagToggle flag="allergy" active={form.flags.includes('allergy')} onToggle={toggleFlag} translate={tp} />
                          <FlagToggle flag="high-risk" active={form.flags.includes('high-risk')} onToggle={toggleFlag} translate={tp} />
                          <FlagToggle flag="vip" active={form.flags.includes('vip')} onToggle={toggleFlag} translate={tp} />
                        </div>
                      </ModalSection>
                    )}

                    {activeModalTab === 'clinical' && (
                      <ModalSection
                        title={tp('Clinical notes')}
                        subtitle={tp('Allergies, flags, and internal notes for intake context')}
                      >
                        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
                          <TextField
                            label={tp('Allergies')}
                            placeholder={tp('Comma separated')}
                            value={form.allergies}
                            onChange={(v) => setForm({ ...form, allergies: v })}
                          />
                          {createFieldSettings.clinicalNotes.visible && (
                            <label className="space-y-1 text-sm">
                              <span className="text-slate-500">{tp('Notes')}{createFieldSettings.clinicalNotes.required ? ' *' : ''}</span>
                              <textarea
                                className="min-h-[140px] w-full rounded-lg border border-slate-200 px-3 py-2"
                                rows={5}
                                value={form.clinicalNotes}
                                onChange={(e) => setForm({ ...form, clinicalNotes: e.target.value })}
                              />
                            </label>
                          )}
                        </div>
                      </ModalSection>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
              <button
                className="rounded-lg border border-slate-200 bg-white px-4 py-2"
                onClick={resetPatientModal}
                disabled={saving}
              >
                {tp('Cancel')}
              </button>
              <button
                className="rounded-lg bg-ink px-4 py-2 text-white shadow-soft disabled:opacity-60"
                onClick={savePatient}
                disabled={saving || modalLoading}
              >
                {saving ? tp('Saving...') : tp('Save Patient')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function ModalTabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-xl px-4 py-2 text-sm font-medium transition-colors',
        active ? 'bg-ink text-white shadow-soft' : 'text-slate-600 hover:bg-slate-100 hover:text-ink',
      )}
    >
      {label}
    </button>
  );
}

function ModalSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4">
        <h4 className="text-base font-semibold text-ink">{title}</h4>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {children}
    </section>
  );
}

type ToolbarProps = {
  statusFilter: PatientStatus | 'All';
  setStatusFilter: (s: PatientStatus | 'All') => void;
  providerFilter: string;
  setProviderFilter: (p: string) => void;
  providerOptions: Array<{ id: string; name: string }>;
  sexFilter: 'All' | Sex;
  setSexFilter: (s: 'All' | Sex) => void;
  ageRange: [number, number];
  setAgeRange: (v: [number, number]) => void;
  sortBy: string;
  setSortBy: (v: any) => void;
  bulkAction: string;
  setBulkAction: (v: string) => void;
  translate: PhraseTranslator;
};

function Toolbar(props: ToolbarProps) {
  const t = props.translate;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
      <button className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 hover:bg-slate-50">
        <Filter size={16} /> {t('Filters')}
      </button>
      <select
        className="text-sm border border-slate-300 rounded-xl px-3 py-2 bg-white"
        value={props.statusFilter}
        onChange={(e) => props.setStatusFilter(e.target.value as PatientStatus | 'All')}
      >
        <option value="All">{t('All status')}</option>
        {statuses.map((s) => (
          <option key={s} value={s}>
            {localizePatientStatus(s, t)}
          </option>
        ))}
      </select>
      <select
        className="text-sm border border-slate-300 rounded-xl px-3 py-2 bg-white"
        value={props.providerFilter}
        onChange={(e) => props.setProviderFilter(e.target.value)}
      >
        <option value="All">{t('All providers')}</option>
        {props.providerOptions.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name || t('Provider')}
          </option>
        ))}
      </select>
      <select
        className="text-sm border border-slate-300 rounded-xl px-3 py-2 bg-white"
        value={props.sexFilter}
        onChange={(e) => props.setSexFilter(e.target.value as 'All' | Sex)}
      >
        <option value="All">{t('All sex')}</option>
        <option value="F">{t('Female')}</option>
        <option value="M">{t('Male')}</option>
        <option value="O">{t('Other')}</option>
      </select>
      <div className="text-xs text-slate-500 flex items-center gap-2">
        {t('Age')}
        <input
          type="number"
          min={0}
          value={props.ageRange[0]}
          onChange={(e) => props.setAgeRange([Number(e.target.value), props.ageRange[1]])}
          className="w-16 border border-slate-300 rounded-xl px-2 py-1 bg-white"
        />
        <span>-</span>
        <input
          type="number"
          min={0}
          value={props.ageRange[1]}
          onChange={(e) => props.setAgeRange([props.ageRange[0], Number(e.target.value)])}
          className="w-16 border border-slate-300 rounded-xl px-2 py-1 bg-white"
        />
      </div>
      <select
        className="text-sm border border-slate-300 rounded-xl px-3 py-2 bg-white"
        value={props.sortBy}
        onChange={(e) => props.setSortBy(e.target.value)}
      >
        <option value="name">{t('Sort: Name')}</option>
        <option value="mrn">{t('Sort: MRN')}</option>
        <option value="lastVisit">{t('Sort: Last visit')}</option>
        <option value="created">{t('Sort: Created')}</option>
      </select>
      <select
        className="text-sm border border-slate-300 rounded-xl px-3 py-2 bg-white"
        value={props.bulkAction}
        onChange={(e) => props.setBulkAction(e.target.value)}
      >
        <option value="none">{t('Bulk actions')}</option>
        <option value="message">{t('Message selected')}</option>
        <option value="export">{t('Export')}</option>
        <option value="archive">{t('Archive')}</option>
      </select>
    </div>
  );
}

type TableProps = {
  patients: PatientRow[];
  onSelect: (id: string) => void;
  onEdit: (patient: PatientRow) => void;
  onDeactivate: (patient: PatientRow) => void;
  onReactivate: (patient: PatientRow) => void;
  onDelete: (patient: PatientRow) => void;
  onMerge: (patient: PatientRow) => void;
  onReferral: (patient: PatientRow) => void;
  busyId: string | null;
  translate: PhraseTranslator;
  locale: string;
};

function PatientTable({ patients, onSelect, onEdit, onDeactivate, onReactivate, onDelete, onMerge, onReferral, busyId, translate, locale }: TableProps) {
  const t = translate;
  const sexLabel = (sex: Sex) => {
    if (sex === 'F') return t('Female');
    if (sex === 'M') return t('Male');
    return t('Other');
  };

  return (
    <div className="overflow-auto rounded-2xl border border-slate-200">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
          <tr>
            <th className="px-3 py-3 text-left">{t('Patient')}</th>
            <th className="px-3 py-3 text-left">{t('MRN')}</th>
            <th className="px-3 py-3 text-left">{t('Age / Sex')}</th>
            <th className="px-3 py-3 text-left">{t('Contact')}</th>
            <th className="px-3 py-3 text-left">{t('Status')}</th>
            <th className="px-3 py-3 text-left">{t('Last visit')}</th>
            <th className="px-3 py-3 text-left">{t('Primary provider')}</th>
            <th className="px-3 py-3 text-left">{t('Flags')}</th>
            <th className="px-3 py-3 text-left">{t('Labels')}</th>
            <th className="px-3 py-3 text-left">{t('Communication')}</th>
            <th className="px-3 py-3 text-right">{t('Actions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {patients.map((p) => {
            const avatar = p.avatarUrl || (p.avatarData as string | undefined);
            return (
              <tr
                key={p.id}
                className="hover:bg-slate-50 cursor-pointer"
                onClick={() => onSelect(p.id)}
              >
                <td className="px-3 py-3">
                  <div className="flex items-center gap-3">
                    <InitialAvatar name={`${p.firstName} ${p.lastName}`} src={avatar} alt={`${p.firstName} ${p.lastName}`} className="h-10 w-10" />
                    <div>
                      <p className="font-semibold text-ink">{`${p.firstName} ${p.lastName}`}</p>
                      <p className="text-xs text-slate-500">{p.address}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-slate-600">{p.mrn}</td>
                <td className="px-3 py-3 text-slate-600">
                  {p.age.toLocaleString(locale)} / {sexLabel(p.sex)}
                </td>
                <td className="px-3 py-3 text-slate-600">
                  <div className="flex items-center gap-2">
                    <Phone size={14} className="text-slate-400" />
                    {p.phone}
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 text-xs">
                    <Mail size={12} />
                    {p.email}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <span className={cn('pill', statusColors[p.status])}>{localizePatientStatus(p.status, t)}</span>
                </td>
                <td className="px-3 py-3 text-slate-600">{formatPatientDateTime(p.lastVisit, locale, t)}</td>
                <td className="px-3 py-3 text-slate-600 flex items-center gap-1">
                  <Stethoscope size={14} className="text-sky-500" />
                  {p.provider || t('Unassigned')}
                </td>
                <td className="px-3 py-3">
                  <FlagIcons flags={p.flags} />
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1">
                    {p.flags.includes('vip') ? <PatientLabel label="VIP" tone="amber" /> : null}
                    {p.flags.includes('high-risk') ? <PatientLabel label={t('High risk')} tone="rose" /> : null}
                    {p.insuranceProvider ? <PatientLabel label={p.insuranceProvider} tone="sky" /> : null}
                    {!p.flags.length && !p.insuranceProvider ? <span className="text-xs text-slate-400">{t('None listed')}</span> : null}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1">
                    <PatientLabel label={p.email ? t('Email') : t('No email')} tone={p.email ? 'emerald' : 'slate'} />
                    <PatientLabel label={p.phone ? t('SMS') : t('No SMS')} tone={p.phone ? 'emerald' : 'slate'} />
                  </div>
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button
                      className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-ink"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(p.id);
                      }}
                    >
                      {t('Chart')}
                    </button>
                    <button
                      className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-ink"
                      title={t('Client Referrals')}
                      onClick={(e) => {
                        e.stopPropagation();
                        onReferral(p);
                      }}
                    >
                      <Stethoscope size={16} />
                    </button>
                    <button
                      className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-ink"
                      title={t('Merge Clients')}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMerge(p);
                      }}
                    >
                      <GitMerge size={16} />
                    </button>
                    <button
                      className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-ink disabled:opacity-50"
                      title={p.status === 'INACTIVE' ? t('Reactivate Client') : t('Deactivate Client')}
                      disabled={busyId === p.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        p.status === 'INACTIVE' ? onReactivate(p) : onDeactivate(p);
                      }}
                    >
                      {p.status === 'INACTIVE' ? <UserCheck size={16} /> : <UserX size={16} />}
                    </button>
                    <button
                      className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-ink"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(p);
                      }}
                    >
                      <Settings size={16} />
                    </button>
                    <button
                      className="px-2 py-1.5 rounded-lg border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                      title={t('Delete Client')}
                      disabled={busyId === p.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(p);
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {patients.length === 0 && (
        <div className="p-6 text-center text-slate-500">{t('No patients match your filters yet.')}</div>
      )}
    </div>
  );
}

function PatientLabel({ label, tone }: { label: string; tone: 'amber' | 'rose' | 'sky' | 'emerald' | 'slate' }) {
  const classes = {
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100',
    sky: 'bg-sky-50 text-sky-700 border-sky-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    slate: 'bg-slate-50 text-slate-500 border-slate-100',
  };
  return <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${classes[tone]}`}>{label}</span>;
}

function FlagToggle({
  flag,
  active,
  onToggle,
  translate,
}: {
  flag: PatientRow['flags'][number];
  active: boolean;
  onToggle: (flag: PatientRow['flags'][number]) => void;
  translate: PhraseTranslator;
}) {
  const label = flag === 'allergy' ? translate('Allergy') : flag === 'high-risk' ? translate('High risk') : 'VIP';
  return (
    <button
      type="button"
      className={cn(
        'px-3 py-1.5 rounded-lg border text-xs',
        active ? 'border-ink text-ink bg-ink/5' : 'border-slate-200 text-slate-600',
      )}
      onClick={() => onToggle(flag)}
    >
      {label}
    </button>
  );
}

function Pagination({
  current,
  total,
  onPageChange,
  translate,
}: {
  current: number;
  total: number;
  onPageChange: (page: number) => void;
  translate: PhraseTranslator;
}) {
  return (
    <div className="flex items-center justify-between text-sm text-slate-500 mt-3">
      <p>
        {translate('Page')} {current} {translate('of')} {total}
      </p>
      <div className="flex items-center gap-2">
        <button
          className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-50"
          onClick={() => onPageChange(Math.max(1, current - 1))}
          disabled={current <= 1}
        >
          {translate('Prev')}
        </button>
        <button
          className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white disabled:opacity-50"
          onClick={() => onPageChange(Math.min(total, current + 1))}
          disabled={current >= total}
        >
          {translate('Next')}
        </button>
      </div>
    </div>
  );
}
